import { useState, useRef, useEffect, useMemo, useCallback, memo, CSSProperties } from "react";
import { CustomFeature, CustomLatLng } from "core/features/maps/typings";
import { InfoWindow, OverlayView } from "@react-google-maps/api";
import { FaEye, FaRegCopy, FaPaste, FaUnlink } from "react-icons/fa";
import Swal from "sweetalert2";
import { showError, showSuccess, showConfirm } from "infra/helpers/alerta";
import http from "infra/http";
import URL from "core/resources/urls";
import ModalDetailsTalhao from "./components/ModalDetailsTalhao";
import LoadingCard from "../../LoadingCard";
import { CurrentViewTypeEnum, PointsBaseMap } from "./typings";
import { MarkersContainer, StyledButton } from "./styles";
import { GeometryType } from "./typings";
import useTalhoesStore from "./hooks/useTalhoesStore";
import useMapData from "../../../hooks/useMapData";
import { useFilters } from "core/features/filters/store";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";
import { Tooltip } from "antd";
import useMapAmount from "./hooks/useMapAmount";
import useCancelarVinculoPlanejamentosTalhoes from "./hooks/useCancelarVinculoPlanejamentosTalhoes";
import {
  evaluateDesvincularEligibility,
  formatTalhoesList,
  updateMapAfterDesvincular,
} from "./hooks/desvincularPlanoUtils";
import { useTalhaoBoxSelect } from "./hooks/useTalhaoBoxSelect";
import { buildTalhaoBoundsIndex, TalhaoBounds } from "./hooks/boxSelectUtils";
import BoxSelectOverlay from "./components/BoxSelectOverlay";

const getCodigoLabelOffset = (width: number, height: number) => ({
  x: -width / 2,
  y: -height / 2,
});

const getAmountBadgeOffset = (width: number, height: number) => ({
  x: width * 0.6,
  y: -height,
});

const LABEL_STYLE_DEFAULT: CSSProperties = {
  width: 30,
  height: 30,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontSize: 14,
  borderRadius: "50%",
  color: "#fff",
  fontWeight: "bold",
  backgroundColor: "transparent",
};

const LABEL_STYLE_BOX: CSSProperties = {
  ...LABEL_STYLE_DEFAULT,
  color: "#000",
  fontWeight: "normal",
  backgroundColor: "#fff",
};

const AMOUNT_BADGE_STYLE: CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: 15,
  color: "#fff",
  fontWeight: "bold",
  backgroundColor: "#ff5722",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

function countLinkedPlanos(
  planos: any[] | undefined,
  planoOperacionalIds: { id: string }[]
): number {
  if (!planos?.length || !planoOperacionalIds.length) return 0;

  const filteredPlanos = planos.filter(
    (item: any, index: number, array: any[]) =>
      index ===
      array.findIndex((i) => i.PlanoOperacionalId === item.PlanoOperacionalId)
  );

  return filteredPlanos.filter((p: any) =>
    planoOperacionalIds.some((o) => o.id === p.PlanoOperacionalId)
  ).length;
}

interface TalhaoProps {
  talhao: any;
  currentViewType: CurrentViewTypeEnum;
  displayMarkers: boolean;
  zoomAllowsMarkers: boolean;
}

const Talhao = memo(function Talhao({
  talhao,
  currentViewType,
  displayMarkers,
  zoomAllowsMarkers,
}: TalhaoProps) {
  const planoOperacionalIds = useTalhoesStore(
    (s: { planoOperacionalIds: { id: string }[] }) => s.planoOperacionalIds
  );

  const amount = useMemo(
    () =>
      countLinkedPlanos(
        talhao.properties?.planosoperacionais,
        planoOperacionalIds
      ),
    [talhao.properties?.planosoperacionais, planoOperacionalIds]
  );

  const position = useMemo(
    () => ({
      lat: talhao.properties?.pontoCentralLat,
      lng: talhao.properties?.pontoCentralLong,
    }),
    [talhao.properties?.pontoCentralLat, talhao.properties?.pontoCentralLong]
  );

  const keepCodigoMounted =
    zoomAllowsMarkers && currentViewType !== CurrentViewTypeEnum.transparent;
  const keepAmountMounted = zoomAllowsMarkers && amount > 1;
  const codigoVisible = displayMarkers && keepCodigoMounted;
  const amountVisible = displayMarkers && keepAmountMounted;
  const labelStyle =
    currentViewType === CurrentViewTypeEnum.box
      ? LABEL_STYLE_BOX
      : LABEL_STYLE_DEFAULT;

  if (!keepCodigoMounted && !keepAmountMounted) {
    return null;
  }

  return (
    <>
      {keepCodigoMounted && (
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={getCodigoLabelOffset}
        >
          <p
            style={{
              ...labelStyle,
              visibility: codigoVisible ? "visible" : "hidden",
              pointerEvents: "none",
            }}
          >
            {talhao.properties?.codigo}
          </p>
        </OverlayView>
      )}

      {keepAmountMounted && (
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={getAmountBadgeOffset}
        >
          <div
            style={{
              ...AMOUNT_BADGE_STYLE,
              visibility: amountVisible ? "visible" : "hidden",
              pointerEvents: "none",
            }}
          >
            {amount}
          </div>
        </OverlayView>
      )}
    </>
  );
});

interface BaseMapProps {
  mapRef: any;
  displayMarkers: boolean;
  zoomAllowsMarkers?: boolean;
  hideMarkers: () => void;
  restoreMarkers: () => void;
  currentViewType: CurrentViewTypeEnum;
  refetch: any;
  points?: PointsBaseMap;
  onClickItem?: (latLng: CustomLatLng, feature: CustomFeature) => void;
}

function BaseMap({
  mapRef,
  displayMarkers,
  zoomAllowsMarkers = true,
  currentViewType,
  refetch,
  points,
}: BaseMapProps) {
  const [menuPosition, setMenuPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [talhaoId, setTalhaoId] = useState<string>();
  const [talhaoCodigo, setTalhaoCodigo] = useState<string>();
  const [isReliable, setIsReliable] = useState(false);
  const [origemTalhaoId, setOrigemTalhaoId] = useState<string>();
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [isDesvinculando, setIsDesvinculando] = useState(false);
  const { hasPermission } = useValidatePermission();

  const { filters: { tabBarSessaoPlanejamentoOperacional } } = useFilters();
  const selectedTalhoesRef = useRef<Set<string>>(new Set());
  const boundsByIdRef = useRef<Map<string, TalhaoBounds>>(new Map());
  const talhaoOcupacaoByIdRef = useRef<Map<string, string>>(new Map());
  const { setListTalhoesIds } = useMapData();
  const { setAmount } = useMapAmount();
  const { mutateAsync: cancelarVinculo } = useCancelarVinculoPlanejamentosTalhoes();

  const { planoOperacionalIds, talhaoIds, setTalhaoIds } = useTalhoesStore();

  const desvincularEligibility = useMemo(() => {
    return evaluateDesvincularEligibility(
      mapRef.current,
      planoOperacionalIds,
      talhaoIds
    );
  }, [mapRef, planoOperacionalIds, talhaoIds, menuPosition]);

  const canDesvincular =
    hasPermission(["PlanoOperacional.DesvincularTalhao"]) &&
    desvincularEligibility.canDesvincular;

  const handleBoxSelect = useCallback(
    (talhaoIds: string[]) => {
      const { planoOperacionalIds: planos } = useTalhoesStore.getState();
      if (planos.length === 0 || talhaoIds.length === 0) return;

      const ocupacoesSet = new Set(planos.map((p) => p.ocupacao));
      const boxIds = new Set(talhaoIds);
      const ocupacaoById = talhaoOcupacaoByIdRef.current;

      setTalhaoIds((prev) => {
        const existing = new Set(prev.map((p) => p.id));
        const kept = prev.filter((p) => !boxIds.has(p.id));
        const toAdd = talhaoIds
          .map((id) => ({
            id,
            ocupacao: ocupacaoById.get(id) ?? "",
          }))
          .filter(
            (t) =>
              t.id &&
              !existing.has(t.id) &&
              t.ocupacao &&
              ocupacoesSet.has(t.ocupacao)
          );

        return [...kept, ...toAdd];
      });
    },
    [setTalhaoIds]
  );

  const { boxRect, skipNextClickRef } = useTalhaoBoxSelect({
    mapRef,
    boundsByIdRef,
    enabled: tabBarSessaoPlanejamentoOperacional === "cadastros",
    onSelect: handleBoxSelect,
  });

  function clearSelection() {
    selectedTalhoesRef.current.clear();
    if (!mapRef.current) return;
    const map = mapRef.current;
    setListTalhoesIds(new Set());
    map.data.forEach((f: any) => f.setProperty("selected", false));
  }

  useEffect(() => {
    if (!mapRef.current || !points) return;

    const map = mapRef.current;

    map.data.forEach((f: any) => map.data.remove(f));

    map.data.addGeoJson(points as any);

    boundsByIdRef.current = buildTalhaoBoundsIndex(
      (points.features ?? []) as Parameters<typeof buildTalhaoBoundsIndex>[0]
    );
    const ocupacaoById = new Map<string, string>();
    for (const feature of points.features ?? []) {
      const id = feature.properties?.id;
      const ocupacao = feature.properties?.ocupacaoNome;
      if (id != null && id !== "" && ocupacao) {
        ocupacaoById.set(String(id), String(ocupacao));
      }
    }
    talhaoOcupacaoByIdRef.current = ocupacaoById;

    map.data.setStyle((feature: any) => {
      const stroke = feature.getProperty("stroke");
      const strokeWidth = 2;
      const strokeOpacity = Number(feature.getProperty("strokeOpacity")) || 1;

      const fill = tabBarSessaoPlanejamentoOperacional === 'cadastros' ? "transparent" : feature.getProperty("fill");
      const fillOpacity = tabBarSessaoPlanejamentoOperacional === 'cadastros' ? 0 : Number(feature.getProperty("fillOpacity"));

      const isSelected = feature.getProperty("selected");

      if (isSelected) {
        return {
          fillColor: feature.getProperty("fill"),
          fillOpacity: 0.8,
          strokeColor: "#FF0000",
          strokeWeight: 2,
          zIndex: 1000,
        };
      }

      if (!fill) {
        return {
          strokeColor: stroke || "#000",
          strokeWeight: strokeWidth,
          strokeOpacity: strokeOpacity,
          fillOpacity: 0,
        };
      }

      return {
        strokeColor: stroke || "#000",
        strokeWeight: strokeWidth,
        strokeOpacity: strokeOpacity,
        fillColor: fill,
        fillOpacity: isNaN(fillOpacity) ? 0.6 : fillOpacity,
      };
    });

  }, [points, tabBarSessaoPlanejamentoOperacional]);

  function handleClick(event: any) {
    if (skipNextClickRef.current) {
      return;
    }

    const { talhaoIds, planoOperacionalIds } = useTalhoesStore.getState();

    if (planoOperacionalIds.length > 0) {
      const feature = event.feature;
      const clickedTalhaoId = feature.getProperty("id");
      const talhaoOcupacaoNome = feature.getProperty("ocupacaoNome");

      if (clickedTalhaoId && talhaoIds.some(p => p.id === clickedTalhaoId)) {
        setTalhaoIds((prev) => prev.filter(p => p.id !== clickedTalhaoId));
        return;
      }

      // Transparente / ocupação incompatível: não selecionável
      const ocupacoesSet = new Set(planoOperacionalIds.map((p) => p.ocupacao));
      if (!talhaoOcupacaoNome || !ocupacoesSet.has(talhaoOcupacaoNome)) {
        return;
      }

      setTalhaoIds((prev) => [...prev, { id: clickedTalhaoId, ocupacao: talhaoOcupacaoNome }]);
    }
  }

  function handleRightClick(event: any) {
    event.domEvent?.preventDefault();

    const feature = event.feature;
    const nextTalhaoId = feature.getProperty("id");
    const nextTalhaoCodigo = feature.getProperty("codigo");
    const talhaoPlanos = feature.getProperty("planosoperacionais");
    const position = { lat: event.latLng.lat(), lng: event.latLng.lng() }

    if (!position) return;
    setMenuPosition({ lat: position.lat, lng: position.lng });
    setIsReliable(talhaoPlanos?.length > 0);
    setTalhaoCodigo(nextTalhaoCodigo);
    setTalhaoId(nextTalhaoId);
  }

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const click = map.data.addListener("click", handleClick);
    const dbl = map.data.addListener("dblclick", handleRightClick);
    const right = map.data.addListener("rightclick", handleRightClick);

    return () => {
      google.maps.event.removeListener(click);
      google.maps.event.removeListener(dbl);
      google.maps.event.removeListener(right);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapRef.current) return;

    if (tabBarSessaoPlanejamentoOperacional === 'cadastros') {
      selectedTalhoesRef.current.clear();
      setListTalhoesIds(new Set());
      map?.data.forEach((f: any) => f.setProperty("selected", false));
    }
  }, [tabBarSessaoPlanejamentoOperacional]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const mapElement = document.getElementById("map-print");
      if (!mapElement) return;
      if (!mapElement.contains(e.target as Node)) {
        clearSelection();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const Polygons =
    points?.features.filter(
      (feature) =>
        feature.geometry.type === GeometryType.Polygon ||
        feature.geometry.type === GeometryType.MultiPolygon
    ) || [];

  function handleCloseMenu() {
    setMenuPosition(null);
  }

  function handleOpenDetails() {
    setIsDetailsOpen(true);
    handleCloseMenu();
  }

  function handleCopyPlanning() {
    setOrigemTalhaoId(talhaoId);
    handleCloseMenu();
  }

  function handleUpdateMapAfterCopy() {
    if (!mapRef.current) return;

    let isTalhaoVinculado = false;

    mapRef.current.data.forEach((feature: any) => {
      const id = feature.getProperty("id");
      const planos = feature.getProperty("planosoperacionais");

      if (id === origemTalhaoId) {
        isTalhaoVinculado = planos.some((p: any) => planoOperacionalIds.some(i => i.id === p.PlanoOperacionalId));
      }
    });

    mapRef.current.data.forEach((feature: any) => {
      const id = feature.getProperty("id");

      if (id === talhaoId && planoOperacionalIds.length > 0 && isTalhaoVinculado) {
        mapRef.current!.data.overrideStyle(feature, {
          fillColor: "#009157",
          fillOpacity: 1,
          strokeColor: "#FFFFFF",
          strokeOpacity: 1,
          strokeWeight: 2,
        });
      }
    });

    handleCloseMenu();
  }

  async function handlePastePlanning() {
    if (origemTalhaoId && talhaoId) {
      try {
        await http.put(URL.PLANO_OPERACIONAL.COPIAR_PLANEJAMENTO(origemTalhaoId, talhaoId));
        handleUpdateMapAfterCopy();
        showSuccess("Planejamentos colados com sucesso");
      } catch (error) {
        console.log(error);
        showError("Erro ao colar os planejamentos");
      }
    }
  }

  async function handleDesvincularPlano() {
    if (!canDesvincular || !mapRef.current) return;

    const eligibility = evaluateDesvincularEligibility(
      mapRef.current,
      planoOperacionalIds,
      talhaoIds
    );

    if (!eligibility.canDesvincular || !eligibility.planoId) return;

    handleCloseMenu();

    const result = await showConfirm(
      "Deseja desvincular o planejamento dos talhões?"
    );
    if (!result.isConfirmed) return;

    const affectedTalhaoIds = talhaoIds.map((t) => t.id);
    const { idsToDelete, codigosTalhoes, planoId } = eligibility;

    setIsDesvinculando(true);
    try {
      await cancelarVinculo({
        planoOperacionalOperacaoTalhaoIds: idsToDelete,
      });

      updateMapAfterDesvincular(mapRef.current, affectedTalhaoIds, planoId);

      affectedTalhaoIds.forEach((id) => setAmount(id, 0));
      setTalhaoIds([]);

      await refetch();
      setIsDesvinculando(false);

      const lista = formatTalhoesList(codigosTalhoes);
      await Swal.fire({
        icon: "success",
        title: "Sucesso",
        iconColor: "#009540",
        confirmButtonText: "OK",
        showCancelButton: false,
        confirmButtonColor: "#009540",
        html: `
          <p><b>Talhões desvinculados com sucesso.</b></p>
          <br/>
          <p>Talhões: ${lista}.</p>
        `,
      });
    } catch (error) {
      console.log(error);
      setIsDesvinculando(false);
      showError("Erro ao desvincular o planejamento dos talhões");
    } finally {
      setIsDesvinculando(false);
    }
  }

  return (
    <>
      {isDesvinculando && <LoadingCard />}
      <BoxSelectOverlay boxRect={boxRect} />
      {talhaoId && talhaoCodigo && isDetailsOpen && (
        <ModalDetailsTalhao
          refetch={refetch}
          mapRef={mapRef}
          isOpen={isDetailsOpen}
          talhaoId={talhaoId}
          talhaoCodigo={talhaoCodigo}
          handleClose={() => setIsDetailsOpen(false)}
        />
      )}

      <MarkersContainer>
        {Polygons.map((item, index) => (
          <Talhao
            key={
              item.properties?.id ||
              item.properties?.codigo ||
              `talhao-${index}-${item.properties?.linkedPlans ?? ""}`
            }
            talhao={item}
            currentViewType={currentViewType}
            displayMarkers={displayMarkers}
            zoomAllowsMarkers={zoomAllowsMarkers}
          />
        ))}

        {menuPosition && (
          <InfoWindow
            position={menuPosition}
            onCloseClick={handleCloseMenu}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 220 }}>
              <StyledButton
                icon={<FaEye />}
                onClick={handleOpenDetails}
                disabled={!isReliable}
              >Visualizar Planejamentos</StyledButton>
              <Tooltip title={!hasPermission(['PlanoOperacional.Copiar']) ? '" Você não tem permissão para copiar planejamentos"' : undefined}>
                <span style={{ width: "100%", display: "block" }}>
                  <StyledButton
                    icon={<FaRegCopy />}
                    onClick={handleCopyPlanning}
                    disabled={!talhaoId || !isReliable || !hasPermission(['PlanoOperacional.Copiar'])}
                  >Copiar Planejamentos</StyledButton>
                </span>
              </Tooltip>
              <Tooltip title={!hasPermission(['PlanoOperacional.Copiar']) ? '" Você não tem permissão para colar planejamentos"' : undefined}>
                <span style={{ width: "100%", display: "block" }}>
                  <StyledButton
                    icon={<FaPaste />}
                    onClick={handlePastePlanning}
                    disabled={!origemTalhaoId || !talhaoId || !hasPermission(['PlanoOperacional.Copiar'])}
                  >Colar Planejamentos</StyledButton>
                </span>
              </Tooltip>
              {planoOperacionalIds.length >= 1 && (
                <StyledButton
                  icon={<FaUnlink />}
                  onClick={handleDesvincularPlano}
                  disabled={!canDesvincular}
                >
                  Desvincular plano
                </StyledButton>
              )}
            </div>
          </InfoWindow>
        )}
      </MarkersContainer>
    </>
  );
}

export default BaseMap;
